import { getSession } from '@/auth/AuthSession';
import { en, pt } from '@onthebuck/i18n';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/pt';
import calendar from 'dayjs/plugin/calendar';
import isoWeek from 'dayjs/plugin/isoWeek';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import type { CustomDetector } from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

dayjs.extend(calendar);
dayjs.extend(isoWeek);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

const calendarFormats = {
	sameDay: '[Today]',
	lastDay: '[Yesterday]',
	lastWeek: 'MMM D, YYYY',
	sameElse: 'MMM D, YYYY',
};

const ptMonthsShort = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function configureDayjsLocale(lng: string) {
	if (lng === 'pt') {
		dayjs.updateLocale('pt', {
			calendar: {
				sameDay: `[${i18n.t('common.today')}]`,
				lastDay: `[${i18n.t('common.yesterday')}]`,
				lastWeek: calendarFormats.lastWeek,
				sameElse: calendarFormats.sameElse,
			},
			monthsShort: ptMonthsShort,
		});
		return;
	}

	if (lng === 'en') {
		dayjs.updateLocale('en', {
			calendar: {
				sameDay: `[${i18n.t('common.today')}]`,
				lastDay: `[${i18n.t('common.yesterday')}]`,
				lastWeek: calendarFormats.lastWeek,
				sameElse: calendarFormats.sameElse,
			},
		});
	}
}

const authSessionDetector: CustomDetector = {
	name: 'authSession',
	lookup(): string | undefined {
		const session = getSession();
		return session?.preferredLang;
	},
	cacheUserLanguage(): void {
		// Not caching here, as the source of truth is the session itself
	},
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(authSessionDetector);

const session = getSession();
const initialLngFromSession = session?.preferredLang;

await i18n
	.use(languageDetector)
	.use(initReactI18next)
	.init({
		lng: initialLngFromSession,
		fallbackLng: 'en',
		debug: true,
		supportedLngs: ['en', 'es', 'fr', 'pt'],
		load: 'languageOnly',

		interpolation: {
			escapeValue: false, // not needed for react as it escapes by default
		},

		detection: {
			order: ['authSession', 'localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
			caches: ['localStorage'],
		},

		resources: {
			en: {
				translation: en,
			},
			pt: {
				translation: pt,
			},
		},
	});

configureDayjsLocale(i18n.language);
dayjs.locale(i18n.language);

i18n.on('languageChanged', lng => {
	configureDayjsLocale(lng);
	dayjs.locale(lng);
});

// we can add more mixins here
i18n.services.formatter?.add('DATE_LONG', (value, lng) => {
	if (!lng) {
		return value;
	}
	return dayjs(value).locale(lng).format('LLLL');
});

i18n.services.formatter?.add('NUMBER', (value, lng, options) => {
	if (!lng || Number.isNaN(value)) {
		return value;
	}
	return new Intl.NumberFormat(lng, options).format(value);
});

export default i18n;
